
import { useState } from "react";
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

interface KitchenGalleryProps {
  photos: string[];
  isVisible: boolean;
  chefName?: string;
}

export const KitchenGallery = ({ photos, isVisible, chefName = "" }: KitchenGalleryProps) => {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  
  if (!isVisible || photos.length === 0) {
    return null;
  }

  const openPhotoModal = (index: number) => {
    setSelectedPhotoIndex(index);
  };

  const closePhotoModal = () => {
    setSelectedPhotoIndex(null);
  };

  const goToNextPhoto = () => {
    if (selectedPhotoIndex === null) return;
    setSelectedPhotoIndex((selectedPhotoIndex + 1) % photos.length);
  };

  const goToPreviousPhoto = () => {
    if (selectedPhotoIndex === null) return;
    setSelectedPhotoIndex((selectedPhotoIndex - 1 + photos.length) % photos.length);
  };

  return (
    <div className="mt-8">
      <h3 className="text-lg font-medium mb-4">Kitchen Gallery</h3>
      <p className="text-muted-foreground mb-4">
        Take a peek inside {chefName ? `${chefName}'s` : "the chef's"} kitchen and food preparation area.
      </p>
      
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {photos.map((photo, index) => (
          <div 
            key={index}
            className="aspect-square relative overflow-hidden rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => openPhotoModal(index)}
          >
            <img 
              src={photo} 
              alt={`Kitchen photo ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>

      <Dialog open={selectedPhotoIndex !== null} onOpenChange={closePhotoModal}>
        <DialogContent className="sm:max-w-3xl p-0 overflow-hidden bg-black/90">
          <div className="relative h-[80vh] flex items-center justify-center">
            {selectedPhotoIndex !== null && photos[selectedPhotoIndex] && (
              <img
                src={photos[selectedPhotoIndex]}
                alt={`Kitchen photo ${selectedPhotoIndex + 1}`}
                className="max-h-full max-w-full object-contain"
              />
            )}
            
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/60 text-white hover:bg-black/80"
              onClick={(e) => {
                e.stopPropagation();
                goToPreviousPhoto();
              }}
            >
              ←
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/60 text-white hover:bg-black/80"
              onClick={(e) => {
                e.stopPropagation();
                goToNextPhoto();
              }}
            >
              →
            </Button>
            
            <DialogClose className="absolute top-2 right-2 rounded-sm opacity-70 text-white hover:opacity-100">
              <X className="h-6 w-6" />
            </DialogClose>
          </div>
          
          <div className="bg-white p-4">
            <p className="text-sm">
              Photo {selectedPhotoIndex !== null ? selectedPhotoIndex + 1 : 0} of {photos.length}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
